// THE BROWSER-SIDE SOURCE for the draft provenance's Developer view.
//
// Every run node's exec record names its code — `src/lib/agentRun.js`,
// `runInitialOutreach#contacted`, `readSentRecipients`, `buildOutreachEmail
// (lib/outreachDraft.js)`… — and the Developer view wants to SHOW that code,
// not describe it. The lambda side is served by GET /enrich/source (the
// deployed handler.py, by symbol). This file is the same thing for the
// browser side: it lazy-imports the raw text of the app's own source files
// (webpack `?raw`, see webpack.config.js — its own chunk, loaded only here) and
// cuts out the named definition.
//
// It is a text extractor, not a parser: it finds the definition's first line
// by pattern and walks braces to its end, ignoring braces inside quotes and
// comments line by line. That is enough for this codebase's shape (top-level
// `function`s and `const`s, and the `name: async () => {` handler tables in
// agentRun.js). When it cannot find a clean end it says so and hands back a
// bounded window from the start line rather than nothing — a reader can still
// scroll the real file from there.
//
// The text shown is what this BUILD was made from — the same source the code
// on screen was compiled from. That is as "the code that ran" as a browser can
// be; there is no deployed copy to fetch elsewhere.

// The files an exec record may name, mapped to their raw imports. Adding a
// file here is the only step: any `name (lib/<file>.js)` reference then
// resolves. Kept explicit — a computed import path would defeat webpack's
// chunking, and the list IS the statement of what the view can show.
const RAW = {
  'src/lib/agentRun.js': () => import(/* webpackChunkName: "src-agentRun" */ './agentRun.js?raw'),
  'src/lib/outreachDraft.js': () =>
    import(/* webpackChunkName: "src-outreachDraft" */ './outreachDraft.js?raw'),
  'src/lib/mailHistory.js': () =>
    import(/* webpackChunkName: "src-mailHistory" */ './mailHistory.js?raw'),
  'src/lib/outreachContacts.js': () =>
    import(/* webpackChunkName: "src-outreachContacts" */ './outreachContacts.js?raw'),
  'src/lib/hubspot.js': () => import(/* webpackChunkName: "src-hubspot" */ './hubspot.js?raw'),
  'src/lib/enrich.js': () => import(/* webpackChunkName: "src-enrich" */ './enrich.js?raw'),
  'src/lib/cadence.js': () => import(/* webpackChunkName: "src-cadence" */ './cadence.js?raw'),
  'src/lib/batchPin.js': () => import(/* webpackChunkName: "src-batchPin" */ './batchPin.js?raw'),
  'src/lib/gmail.js': () => import(/* webpackChunkName: "src-gmail" */ './gmail.js?raw'),
  'src/lib/companyMeta.js': () =>
    import(/* webpackChunkName: "src-companyMeta" */ './companyMeta.js?raw'),
}
const RAW_FILES = Object.keys(RAW)

const _text = new Map()
async function fileText(file) {
  if (!RAW[file]) return null
  if (!_text.has(file)) {
    _text.set(
      file,
      RAW[file]()
        .then((m) => String(m && m.default !== undefined ? m.default : m))
        .catch((err) => {
          _text.delete(file)
          throw err
        }),
    )
  }
  return _text.get(file)
}

// `readMailHistory (lib/mailHistory.js)` → { names: ['readMailHistory'], file:
// 'src/lib/mailHistory.js' }; a bare `buildCompanyGate` → the record's own
// file. Anything with a slash-and-more ("touchLabel / isEmailTouch (…)") names
// every identifier in the list — each ran, each is shown (or reported missing).
export function parseCall(call, defaultFile) {
  const raw = String(call || '').trim()
  const m = raw.match(/^([A-Za-z_$][\w$]*(?:\s*\/\s*[A-Za-z_$][\w$]*)*)\s*(?:\((lib\/[\w./-]+)\))?/)
  if (!m) return null
  const file = m[2] ? `src/${m[2]}` : defaultFile
  return { names: m[1].split('/').map((s) => s.trim()), file }
}

// MASK LITERALS before counting brackets. A real scanner over the whole
// text, not a per-line regex: it walks character by character tracking
// whether it is inside a '…' / "…" string, a `…` template (with ${…} nesting,
// across lines), a regex literal (a `/` in a position where an operand is
// expected, ended by an unescaped `/` outside a [...] class), a // comment or
// a /* … */ comment — and replaces every character inside one of those with
// a space (newlines kept), so the bracket walk below sees only code. This is
// what keeps a regex like /[“"]…[”"]/ or a nested template from shifting the
// count and cutting a function short.
const OPERAND_BEFORE_REGEX = /[(,=:[!&|?{};+\-*%<>~^]$|^$|\b(?:return|typeof|case|in|of|new|delete|void|throw|await|yield)$/
function maskLiterals(text) {
  const src = String(text || '')
  const out = new Array(src.length)
  let i = 0
  let lastCode = '' // trailing non-space code seen so far (for regex detection)
  const templateDepth = [] // stack of ${ depths for nested templates
  const keep = (ch) => {
    out[i] = ch
    if (ch !== ' ' && ch !== '\t' && ch !== '\n' && ch !== '\r') lastCode = (lastCode + ch).slice(-8)
    i += 1
  }
  const blank = (ch) => {
    out[i] = ch === '\n' ? '\n' : ' '
    i += 1
  }
  const readQuoted = (q) => {
    // i is on the opening quote
    blank(src[i])
    while (i < src.length) {
      const ch = src[i]
      if (ch === '\\') {
        blank(ch)
        if (i < src.length) blank(src[i])
        continue
      }
      if (ch === q || (q !== '`' && ch === '\n')) {
        blank(ch)
        return
      }
      if (q === '`' && ch === '$' && src[i + 1] === '{') {
        // Template expression: code again until its matching }.
        blank(ch)
        blank(src[i])
        templateDepth.push(0)
        return // caller resumes code scanning; closing } re-enters the template
      }
      blank(ch)
    }
  }
  const readRegex = () => {
    blank(src[i]) // the opening /
    let inClass = false
    while (i < src.length) {
      const ch = src[i]
      if (ch === '\\') {
        blank(ch)
        if (i < src.length) blank(src[i])
        continue
      }
      if (ch === '\n') {
        blank(ch)
        return
      }
      if (ch === '[') inClass = true
      else if (ch === ']') inClass = false
      else if (ch === '/' && !inClass) {
        blank(ch)
        while (i < src.length && /[a-z]/i.test(src[i])) blank(src[i]) // flags
        return
      }
      blank(ch)
    }
  }
  while (i < src.length) {
    const ch = src[i]
    const next = src[i + 1]
    if (ch === '/' && next === '/') {
      while (i < src.length && src[i] !== '\n') blank(src[i])
      continue
    }
    if (ch === '/' && next === '*') {
      blank(ch)
      blank(src[i])
      while (i < src.length && !(src[i] === '*' && src[i + 1] === '/')) blank(src[i])
      if (i < src.length) {
        blank(src[i])
        blank(src[i])
      }
      continue
    }
    if (ch === "'" || ch === '"' || ch === '`') {
      readQuoted(ch)
      continue
    }
    if (ch === '/' && OPERAND_BEFORE_REGEX.test(lastCode.replace(/\s+$/, ''))) {
      readRegex()
      continue
    }
    if (templateDepth.length) {
      // Inside a ${ … } of a template: track braces to find its end, then
      // resume the template's string part.
      if (ch === '{') templateDepth[templateDepth.length - 1] += 1
      else if (ch === '}') {
        if (templateDepth[templateDepth.length - 1] === 0) {
          templateDepth.pop()
          blank(ch)
          // Back into the template string until its closing backtick (or the
          // next ${, which pushes again).
          while (i < src.length) {
            const c = src[i]
            if (c === '\\') {
              blank(c)
              if (i < src.length) blank(src[i])
              continue
            }
            if (c === '`') {
              blank(c)
              break
            }
            if (c === '$' && src[i + 1] === '{') {
              blank(c)
              blank(src[i])
              templateDepth.push(0)
              break
            }
            blank(c)
          }
          continue
        }
        templateDepth[templateDepth.length - 1] -= 1
      }
    }
    keep(ch)
  }
  return out.join('')
}

// From `start` (a line that opens a definition), walk the MASKED lines to the
// line where the bracket depth — {}, [] and () together — returns to zero.
// -1 when it never does within `limit` lines. A definition whose first line
// opens no bracket (`const X =\n  'value'`, a one-line arrow) ends at the last
// consecutive indented line after it, or on its own line.
function endOfBlock(masked, start, limit = 900, raw = masked) {
  let depth = 0
  let opened = false
  const stop = Math.min(masked.length, start + limit)
  for (let i = start; i < stop; i += 1) {
    const l = masked[i]
    for (const ch of l) {
      if (ch === '{' || ch === '[' || ch === '(') {
        depth += 1
        opened = true
      } else if (ch === '}' || ch === ']' || ch === ')') depth -= 1
    }
    if (opened && depth <= 0) return i
    if (!opened && i === start) {
      // No bracket on the opening line: the value continues on the indented
      // lines that follow (if any), and ends with the last of them.
      let j = i
      while (j + 1 < stop && /^\s+\S/.test(raw[j + 1])) j += 1
      if (j === i) return i
      // …unless one of those lines opened a bracket, in which case fall
      // through and count from there.
      const opensLater = masked.slice(i + 1, j + 1).some((m) => /[{[(]/.test(m))
      if (!opensLater) return j
    }
  }
  return -1
}

// The comment block sitting directly above a definition — this codebase
// explains itself there, and the reader should get it with the code.
function leadingComment(lines, start) {
  let i = start
  while (i > 0 && /^\s*(\/\/|\*|\/\*)/.test(lines[i - 1])) i -= 1
  return i
}

const escapeRe = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

/**
 * The source of one browser-side symbol.
 *
 * @param {string} file   e.g. 'src/lib/agentRun.js'
 * @param {string} name   'buildOutreachEmail' | 'runInitialOutreach#contacted'
 * @returns {Promise<{file, name, line, endLine, source, partial?: boolean}|null>}
 *   null when the file is not one this view can show or the name is not found.
 */
export async function browserSymbolSource(file, name) {
  const text = await fileText(file)
  if (!text) return null
  return extractSymbol(text, name, file)
}

/** Pure: cut one named definition out of a file's text. See browserSymbolSource. */
export function extractSymbol(text, name, file = '') {
  const lines = String(text || '').split('\n')
  const masked = maskLiterals(text).split('\n')
  const [outer, inner] = String(name).split('#')

  // Top-level definition: function / async function / const / export …
  const topRe = new RegExp(
    `^(?:export\\s+)?(?:default\\s+)?(?:async\\s+)?(?:function\\s+${escapeRe(outer)}\\s*\\(|(?:const|let|var)\\s+${escapeRe(outer)}\\s*=)`,
  )
  let start = lines.findIndex((l) => topRe.test(l))
  if (start < 0) return null

  if (inner) {
    // A handler inside a function's table: `contacted: async () => {` or
    // `'hs-match': async () => {`, searched from the enclosing function down.
    const outerEnd = endOfBlock(masked, start, 900, lines)
    const innerRe = new RegExp(`^\\s+'?${escapeRe(inner)}'?\\s*:\\s*(?:async\\s*)?\\(`)
    const stop = outerEnd > 0 ? outerEnd : lines.length
    let hit = -1
    for (let i = start; i < stop; i += 1) {
      if (innerRe.test(lines[i])) {
        hit = i
        break
      }
    }
    if (hit < 0) return null
    start = hit
  }

  const from = leadingComment(lines, start)
  let end = endOfBlock(masked, start, 900, lines)
  let partial = false
  if (end < 0) {
    end = Math.min(lines.length - 1, start + 160)
    partial = true
  }
  return {
    file,
    name,
    line: from + 1,
    endLine: end + 1,
    source: lines.slice(from, end + 1).join('\n'),
    ...(partial ? { partial: true } : {}),
  }
}

/**
 * Every symbol one browser exec record names, resolved against its own file
 * (or the file a call names in parentheses). Missing ones are reported, never
 * invented.
 * @returns {Promise<{symbols: object[], missing: string[]}>}
 */
export async function browserExecSource(rec) {
  const file = String(rec?.file || '')
  const names = []
  if (rec?.fn) names.push({ name: String(rec.fn), file })
  for (const call of Array.isArray(rec?.calls) ? rec.calls : []) {
    const parsed = parseCall(call, file)
    if (!parsed) continue
    for (const name of parsed.names) {
      if (!names.some((n) => n.name === name && n.file === parsed.file)) names.push({ name, file: parsed.file })
    }
  }
  const symbols = []
  const missing = []
  for (const n of names) {
    let hit = null
    try {
      hit = await browserSymbolSource(n.file, n.name)
      // A bare name that is not defined in the record's own file (an import,
      // or a record written before the descriptor named the file): look for a
      // top-level definition in every other file this view can show, so real
      // code is found rather than reported missing.
      if (!hit && !n.name.includes('#')) {
        for (const other of RAW_FILES) {
          if (other === n.file) continue
          hit = await browserSymbolSource(other, n.name)
          if (hit) break
        }
      }
    } catch {
      hit = null
    }
    if (hit) symbols.push(hit)
    else missing.push(n.file === file ? n.name : `${n.name} (${n.file})`)
  }
  return { symbols, missing }
}
